import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck, X as XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@iheart/ui';
import { useWorkspaceStore, useWorkspaces, type Workspace } from '../lib/workspace';
import { useConfirm } from './ConfirmDialog';

/** Friendly grouping heading for a notification kind. */
function categoryFor(kind: string): string {
  if (kind === 'chat.message') return 'Chat messages';
  if (kind === 'task.assigned') return 'Tasks assigned';
  if (kind === 'task.completed') return 'Tasks completed';
  if (kind === 'task.due_soon' || kind === 'task.overdue' || kind === 'reminder.countdown')
    return 'Task reminders';
  if (kind.startsWith('project.')) return 'Projects';
  if (kind === 'engage.assigned') return 'Conversations';
  if (kind.startsWith('publish.')) return 'Publishing';
  if (kind.startsWith('deal.')) return 'Deals';
  if (kind.startsWith('account.') || kind === 'subscription.renewal') return 'Account';
  return 'Other';
}

const CATEGORY_ORDER = [
  'Chat messages',
  'Tasks assigned',
  'Tasks completed',
  'Task reminders',
  'Projects',
  'Conversations',
  'Publishing',
  'Deals',
  'Account',
  'Other',
];

/** Coloured heading band per category so sections stand out in long lists. */
const CATEGORY_STYLE: Record<string, string> = {
  'Chat messages': 'bg-blue-100 text-blue-800',
  'Tasks assigned': 'bg-pink-100 text-pink-800',
  'Tasks completed': 'bg-emerald-100 text-emerald-800',
  'Task reminders': 'bg-amber-100 text-amber-800',
  Projects: 'bg-violet-100 text-violet-800',
  Conversations: 'bg-teal-100 text-teal-800',
  Publishing: 'bg-orange-100 text-orange-800',
  Deals: 'bg-indigo-100 text-indigo-800',
  Account: 'bg-neutral-200 text-neutral-700',
  Other: 'bg-neutral-200 text-neutral-700',
};

function wsInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
import {
  useNotifications,
  useUnreadCount,
  useMarkRead,
  useMarkAllRead,
  type Notification,
} from '../lib/notifications';
import { computeRailPanelPos } from './RailTooltip';
import { TimeAgo } from './TimeAgo';

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ left: number; top?: number; bottom?: number; maxHeight: number }>({
    left: 0,
    maxHeight: 400,
  });
  const { data: countData } = useUnreadCount();
  const count = countData?.unread ?? 0;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  function toggle() {
    if (!open) {
      const r = btnRef.current?.getBoundingClientRect();
      // Fixed + portaled, height-capped to the available space so it always fits
      // the viewport on any screen size (scrolls internally instead of clipping).
      if (r) {
        const gap = 8;
        const margin = 8;
        const panelW = 320; // matches the panel's w-[min(20rem,…)]
        if (r.right + gap + panelW <= window.innerWidth - margin) {
          // Desktop rail: the bell is on the left, so open to its right.
          setCoords(computeRailPanelPos(r));
        } else {
          // Mobile top bar: the bell is near the right edge — opening rightward
          // pushes the panel off-screen. Drop it BELOW the header instead,
          // right-aligned to the trigger and clamped inside the viewport.
          const w = Math.min(panelW, window.innerWidth - 2 * margin);
          const left = Math.max(margin, Math.min(r.right - w, window.innerWidth - w - margin));
          const top = r.bottom + gap;
          setCoords({ left, top, maxHeight: window.innerHeight - top - margin });
        }
      }
    }
    setOpen((v) => !v);
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-label={count > 0 ? `Notifications (${count} unread)` : 'Notifications'}
        aria-expanded={open}
        className="relative flex size-10 items-center justify-center rounded-lg text-neutral-500 transition-colors hover:bg-muted hover:text-body"
      >
        <Bell className="size-5" />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex size-4 items-center justify-center rounded-full bg-pink-500 text-[10px] font-bold text-white">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              ref={panelRef}
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              style={{
                position: 'fixed',
                left: coords.left,
                top: coords.top,
                bottom: coords.bottom,
                maxHeight: coords.maxHeight,
              }}
              className="z-[70] flex w-[min(20rem,calc(100vw-1rem))] flex-col overflow-hidden rounded-xl border border-white/30 bg-white/80 shadow-lg backdrop-blur-2xl shadow-[0_4px_30px_rgba(0,0,0,0.05)]"
            >
              <NotificationDropdown onClose={() => setOpen(false)} />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}

function NotificationDropdown({ onClose }: { onClose: () => void }) {
  const { data, isLoading } = useNotifications();
  const markAllRead = useMarkAllRead();
  const workspaces = useWorkspaces();
  const notifications = data?.data ?? [];

  // Map workspace publicId → workspace (for the logo/brand colour on each row).
  const wsMap = new Map((workspaces.data ?? []).map((w) => [w.publicId, w]));

  // Group by friendly category, preserving newest-first order within each group.
  const grouped = new Map<string, Notification[]>();
  for (const n of notifications) {
    const cat = categoryFor(n.kind);
    const arr = grouped.get(cat) ?? [];
    arr.push(n);
    grouped.set(cat, arr);
  }
  const orderedGroups: Array<[string, Notification[]]> = [
    ...CATEGORY_ORDER.filter((c) => grouped.has(c)).map((c) => [c, grouped.get(c)!] as [string, Notification[]]),
    ...[...grouped.keys()].filter((c) => !CATEGORY_ORDER.includes(c)).map((c) => [c, grouped.get(c)!] as [string, Notification[]]),
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center justify-between border-b border-white/20 bg-white/70 px-4 py-3">
        <h3 className="text-sm font-semibold text-neutral-800">Notifications</h3>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => markAllRead.mutate()}
            title="Mark all read"
            aria-label="Mark all notifications as read"
            className="rounded p-1 text-neutral-400 hover:text-neutral-700"
          >
            <CheckCheck className="size-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close notifications"
            className="rounded p-1 text-neutral-400 hover:text-neutral-700"
          >
            <XIcon className="size-4" />
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading && (
          <div className="p-4 text-center text-sm text-neutral-400">Loading...</div>
        )}
        {!isLoading && notifications.length === 0 && (
          <div className="p-6 text-center text-sm text-neutral-400">
            No notifications yet
          </div>
        )}
        {orderedGroups.map(([category, items]) => (
          <div key={category}>
            <div
              className={cn(
                'sticky top-0 z-10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider',
                CATEGORY_STYLE[category] ?? 'bg-neutral-200 text-neutral-700',
              )}
            >
              {category} · {items.length}
            </div>
            {items.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                workspace={n.workspacePublicId ? wsMap.get(n.workspacePublicId) : undefined}
                onClose={onClose}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function NotificationRow({
  notification,
  workspace,
  onClose,
}: {
  notification: Notification;
  workspace?: Workspace;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const markRead = useMarkRead();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const currentWs = useWorkspaceStore((s) => s.current);
  const setCurrentWs = useWorkspaceStore((s) => s.setCurrent);
  const isUnread = !notification.readAt;
  // Task assignments stand out in red; everything else is the usual pink.
  const isTask = notification.kind.startsWith('task.');

  async function handleClick() {
    // The bell shows notifications from every workspace, but deep links like
    // /projects/:id load under the ACTIVE workspace. If this notification belongs
    // to a different workspace, confirm the switch first (otherwise the target
    // project 404s and the page hangs on "Loading…").
    const targetWs = notification.workspacePublicId;
    if (targetWs && targetWs !== currentWs?.publicId) {
      // Close the panel first so the confirm dialog isn't obscured by it.
      onClose();
      const targetName = notification.workspaceName ?? 'another workspace';
      const ok = await confirm({
        title: 'Change workspace?',
        message: `Heads up — this notification lives in "${targetName}". You're currently in "${
          currentWs?.name ?? 'your current workspace'
        }". Continue and switch into "${targetName}"?`,
        confirmLabel: 'Yes, continue',
        cancelLabel: 'No, keep me here',
        variant: 'switch',
      });
      if (!ok) return;
      const full = qc.getQueryData<Workspace[]>(['workspaces'])?.find((w) => w.publicId === targetWs);
      if (full) setCurrentWs(full);
      if (isUnread) markRead.mutate(notification.id);
      if (notification.link) navigate(notification.link);
      return;
    }
    // Same workspace — go straight there.
    if (isUnread) markRead.mutate(notification.id);
    if (notification.link) {
      navigate(notification.link);
      onClose();
    }
  }

  const wsName = notification.workspaceName ?? workspace?.name ?? null;
  const wsLogo = workspace?.logoUrl ?? null;
  const wsColor = workspace?.brandColor ?? '#a3a3a3';

  return (
    <button
      type="button"
      onClick={() => void handleClick()}
      className={cn(
        'w-full border-b border-white/20 bg-white/70 px-4 py-3 text-left transition-colors hover:bg-neutral-50',
        isUnread && (isTask ? 'bg-red-50/60' : 'bg-pink-50/50'),
      )}
    >
      <div className="flex items-start gap-2.5">
        {/* Workspace logo / brand avatar so it's clear which workspace this is for */}
        <span
          className="mt-0.5 flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-md text-[9px] font-bold text-white"
          style={{ backgroundColor: wsLogo ? undefined : wsColor }}
          title={wsName ?? undefined}
        >
          {wsLogo ? (
            <img src={wsLogo} alt="" className="size-full object-cover" />
          ) : wsName ? (
            wsInitials(wsName)
          ) : null}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {isUnread && (
              <span className={cn('size-2 shrink-0 rounded-full', isTask ? 'bg-red-500' : 'bg-pink-500')} />
            )}
            <p className={cn('truncate text-sm', isUnread ? 'font-medium text-neutral-800' : 'text-neutral-600')}>
              {notification.title}
            </p>
          </div>
          {notification.body && (
            <p className="mt-0.5 truncate text-xs text-neutral-500">{notification.body}</p>
          )}
          <div className="mt-1 flex items-center gap-1.5 text-[10px] text-neutral-400">
            {wsName && <span className="truncate font-medium text-neutral-500">{wsName}</span>}
            {wsName && <span>·</span>}
            <TimeAgo date={notification.createdAt} />
          </div>
        </div>
      </div>
    </button>
  );
}

